import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { addDocument, getDocuments, updateDocument, getNextSaleNumber } from '../services/database';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import { ZXingScanner } from '../components/common/ZXingScanner';
import { PhysicalBarcodeScanner } from '../components/common/PhysicalBarcodeScanner';
// import { useReactToPrint } from 'react-to-print'; // Temporalmente deshabilitado
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Lottie from 'lottie-react';
import addToCartAnimation from '../assets/add-to-cart-success.json';
import shoppingCartAnimation from '../assets/shopping-cart-new.json';
import shoppingCartTitleAnimation from '../assets/shopping-cart-title.json';
import pharmacyStoreAnimation from '../assets/pharmacy-store.json';
import scanningBarcodeAnimation from '../assets/scanning-barcode.json';
import scanningBarcodeButtonAnimation from '../assets/scanning-barcode-button.json';
import { formatCurrency, formatNumber, parseFormattedNumber, formatInputNumber } from '../utils/formatters';
import { shareReceiptPDF } from '../utils/pdfGenerator';

const PageContainer = styled.div`
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  position: relative;
  
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.sm} 0;
  }
`;

const ProductsSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CartSection = styled.div`
  position: relative;
  margin-top: ${props => props.theme.spacing.lg};
  padding: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border-radius: 20px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px -6px rgba(46, 91, 136, 0.08),
    0 24px 60px -16px rgba(46, 91, 136, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.15);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #2E5B88 0%, #4FB3BF 50%, #2E5B88 100%);
    opacity: 0.85;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 18px 12px;
    margin-left: 8px;
    margin-right: 8px;
    border-radius: 16px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  justify-content: center;
  align-items: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: row;
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.sm};
    max-width: 100%;
    padding: 0 ${props => props.theme.spacing.xs};
    width: 100%;
    transition: all 0.3s ease-in-out;
  }
`;

const SearchInput = styled(Input)`
  width: 100%;
  transition: all 0.3s ease-in-out;
  
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    min-width: 500px;
    width: auto;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 1;
    input {
      padding: ${props => props.theme.spacing.sm};
      font-size: 14px;
    }
  }
`;

const ScannerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;
  width: ${props => props.isTyping ? '80px' : '150px'};
  height: ${props => props.isTyping ? '80px' : '150px'};
  flex-shrink: 0;
  
  .lottie-wrapper {
    width: ${props => props.isTyping ? '80px' : '150px'};
    height: ${props => props.isTyping ? '80px' : '150px'};
    transition: all 0.3s ease-in-out;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: ${props => props.isTyping ? '50px' : '100px'};
    height: ${props => props.isTyping ? '50px' : '100px'};
    
    .lottie-wrapper {
      width: ${props => props.isTyping ? '50px' : '100px'};
      height: ${props => props.isTyping ? '50px' : '100px'};
    }
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ProductItem = styled(Card)`
  padding: ${props => props.theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all ${props => props.theme.transitions.fast};
  opacity: ${props => props.disabled ? 0.5 : 1};
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(135, 206, 235, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(135, 206, 235, 0.05) 100%);
    opacity: 0;
    transition: opacity ${props => props.theme.transitions.fast};
  }
  
  &:hover:not(.disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    border-color: rgba(135, 206, 235, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &.low-stock {
    border-left: 4px solid ${props => props.theme.colors.warning};
    background: linear-gradient(90deg, rgba(243, 156, 18, 0.05) 0%, transparent 30%);
  }
  
  &.no-stock {
    border-left: 4px solid ${props => props.theme.colors.error};
    background: linear-gradient(90deg, rgba(231, 76, 60, 0.05) 0%, transparent 30%);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.sm};
    margin: 0 ${props => props.theme.spacing.xs};
  }
`;

const CartItem = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  margin-bottom: 10px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 12px -4px rgba(46, 91, 136, 0.08);
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 200ms ease,
    border-color 200ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: linear-gradient(180deg, #2E5B88 0%, #4FB3BF 100%);
    opacity: 0.7;
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(79, 179, 191, 0.35);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.9) inset,
      0 1px 3px rgba(15, 23, 42, 0.06),
      0 8px 20px -6px rgba(46, 91, 136, 0.15);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const QuantityButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #ffffff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  color: #2E5B88;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.05);
  transition:
    background 180ms ease,
    color 180ms ease,
    transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 180ms ease;

  &:hover {
    background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
    color: #ffffff;
    border-color: transparent;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.25) inset,
      0 4px 12px -3px rgba(46, 91, 136, 0.4);
  }

  &:active {
    transform: scale(0.92);
  }
`;

const TotalSection = styled.div`
  position: relative;
  margin-top: 20px;
  padding: 20px 22px;
  background: linear-gradient(135deg, rgba(46, 91, 136, 0.04) 0%, rgba(79, 179, 191, 0.06) 100%);
  border-radius: 16px;
  border: 1px solid rgba(79, 179, 191, 0.2);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.6) inset,
    0 4px 12px -4px rgba(46, 91, 136, 0.12);
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;

  &:last-child {
    margin-bottom: 0;
  }

  &.total {
    font-size: 22px;
    font-weight: 800;
    padding-top: 14px;
    margin-top: 12px;
    border-top: 1px dashed rgba(46, 91, 136, 0.25);
    letter-spacing: -0.3px;

    span:last-child {
      background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-variant-numeric: tabular-nums;
    }
  }
`;

const Receipt = styled.div`
  padding: 20px;
  max-width: 350px;
  margin: 0 auto;
  font-family: 'Courier New', monospace;
  background: white;
  color: black;
  font-size: 14px;
  
  @media print {
    margin: 0;
    padding: 0;
    max-width: 100%;
    
    * {
      color: black !important;
      background: white !important;
    }
  }
`;

const ReceiptHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    font-size: 18px;
  }
  
  p {
    margin: 2px 0;
    font-size: 12px;
  }
`;

const ReceiptBody = styled.div`
  margin-bottom: 20px;
`;

const ReceiptItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 12px;
`;

const ReceiptFooter = styled.div`
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #000;
  
  p {
    margin: 2px 0;
    font-size: 12px;
  }
`;

const CustomerModalContainer = styled.div`
  min-height: 400px;
  width: 100%;
  box-sizing: border-box;
`;

const CustomerSearchSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CustomerList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  box-sizing: border-box;
`;

const CustomerCard = styled.div`
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  background-color: ${props => props.selected ? 'rgba(135, 206, 235, 0.1)' : props.theme.colors.surface};
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(135, 206, 235, 0.2);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CustomerName = styled.p`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
`;

const CustomerInfo = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 2px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
  }
  
  button {
    flex: 1;
    min-width: 0;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormTitle = styled.h3`
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.lg};
`;

const PublicGeneralButton = styled(Button)`
  margin-bottom: 10px;
  transition: all ${props => props.theme.transitions.normal};
  background: ${props => props.selected ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.selected ? 'white' : '#7F8C8D'};
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : 'transparent'};
  
  &:hover {
    background: ${props => props.selected ? props.theme.colors.primary : 'rgba(135, 206, 235, 0.1)'};
    color: ${props => props.selected ? 'white' : props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const AnimationContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  pointer-events: none;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .success-checkmark {
    width: 80px;
    height: 80px;
    position: relative;
    border-radius: 50%;
    background-color: #4CAF50;
    animation: scaleIn 0.3s ease-out;
    
    &::after {
      content: '';
      position: absolute;
      width: 25px;
      height: 45px;
      border: solid white;
      border-width: 0 6px 6px 0;
      transform: rotate(45deg);
      left: 27px;
      top: 12px;
      animation: checkmark 0.3s ease-out 0.3s forwards;
      opacity: 0;
    }
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes checkmark {
    from {
      opacity: 0;
      transform: rotate(45deg) scale(0);
    }
    to {
      opacity: 1;
      transform: rotate(45deg) scale(1);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  
  &.fade-out {
    animation: fadeOut 0.3s ease-out forwards;
  }
`;

const PageTitleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-bottom: 32px;
  padding: 28px 24px;
  background: linear-gradient(135deg, rgba(46, 91, 136, 0.06) 0%, rgba(79, 179, 191, 0.08) 100%);
  border-radius: 24px;
  border: 1px solid rgba(79, 179, 191, 0.18);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(46, 91, 136, 0.12),
    0 20px 48px -20px rgba(79, 179, 191, 0.2);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 260px;
    height: 260px;
    background: radial-gradient(circle, rgba(79, 179, 191, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: 12px;
    margin-bottom: 16px;
    padding: 16px 14px;
    border-radius: 18px;
  }
`;

const PageTitle = styled.h1`
  position: relative;
  text-align: center;
  font-size: 2.75rem;
  font-weight: 800;
  letter-spacing: -1.5px;
  margin: 0;
  padding-bottom: 14px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2E5B88 40%, #4FB3BF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 64px;
    height: 4px;
    border-radius: 4px;
    background: linear-gradient(90deg, #2E5B88 0%, #4FB3BF 100%);
    box-shadow: 0 2px 10px rgba(79, 179, 191, 0.5);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.75rem;
    padding-bottom: 10px;
    letter-spacing: -0.8px;

    &::after {
      width: 48px;
      height: 3px;
    }
  }
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  border-bottom: 2px solid rgba(135, 206, 235, 0.2);
  position: relative;
  background: ${props => props.hasItems ? 'rgba(135, 206, 235, 0.35)' : 'transparent'};
  border-radius: ${props => props.hasItems ? '12px' : '0'};
  transition: all 0.3s ease-in-out;
  margin: ${props => props.hasItems ? '-24px -24px 24px -24px' : '0 0 24px 0'};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin: ${props => props.hasItems ? '-16px -4px 16px -4px' : '0 0 16px 0'};
    padding: ${props => props.hasItems ? '12px' : '16px'};
  }
  
  .title-section {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.lg};
    flex-direction: row;
  }
  
  h3 {
    font-size: ${props => props.hasItems ? '1rem' : '2rem'};
    font-weight: 700;
    color: ${props => props.theme.colors.text.primary};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease-in-out;
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.hasItems ? '0.875rem' : '1.5rem'};
    }
  }
  
  .cart-count {
    background: linear-gradient(135deg, #87CEEB 0%, #98FB98 100%);
    color: white;
    padding: ${props => props.hasItems ? '4px 12px' : '6px 16px'};
    border-radius: 25px;
    font-size: ${props => props.hasItems ? '12px' : '14px'};
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
    transition: all 0.3s ease-in-out;
  }
`;

export const Sales = () => {
  const { userId, userData } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showPhysicalScanner, setShowPhysicalScanner] = useState(false);
  const [hasPhysicalScanner, setHasPhysicalScanner] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showAddToCartAnimation, setShowAddToCartAnimation] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [isProcessingBarcode, setIsProcessingBarcode] = useState(false);
  const animationRef = useRef(null);
  const [chargeWithTax, setChargeWithTax] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [editingPrice, setEditingPrice] = useState(null);
  const [tempPrice, setTempPrice] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    document: '',
    email: '',
    phone: '',
    address: ''
  });
  const receiptRef = useRef(null);

  useEffect(() => {
    loadProducts();
  }, [userId]);

  // Limpiar animación cuando se desmonte o cambie el estado
  useEffect(() => {
    if (!showAddToCartAnimation && animationRef.current) {
      animationRef.current.style.display = 'none';
    }
  }, [showAddToCartAnimation]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detectar si hay un escáner físico conectado
  useEffect(() => {
    let scannerBuffer = '';
    let scannerTimer = null;
    
    const detectBarcodeScanner = (e) => {
      // Si el foco está en un input de texto, no detectar
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Los escáneres de código de barras envían caracteres muy rápido
      clearTimeout(scannerTimer);
      
      if (e.key && e.key.length === 1) {
        scannerBuffer += e.key;
        
        scannerTimer = setTimeout(() => {
          // Si se recibieron más de 5 caracteres en menos de 100ms, probablemente es un escáner
          if (scannerBuffer.length > 5) {
            setHasPhysicalScanner(true);
          }
          scannerBuffer = '';
        }, 100);
      }
    };
    
    window.addEventListener('keypress', detectBarcodeScanner);
    
    // Función global para cambiar a cámara
    window.useCameraInstead = () => {
      setShowPhysicalScanner(false);
      setTimeout(() => {
        setShowScanner(true);
      }, 100);
    };
    
    return () => {
      window.removeEventListener('keypress', detectBarcodeScanner);
      clearTimeout(scannerTimer);
      delete window.useCameraInstead;
    };
  }, []);

  const loadProducts = async () => {
    if (!userId) return;
    
    try {
      const productsData = await getDocuments(userId, 'products');
      setProducts(productsData.filter(p => p.stock > 0));
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadCustomers = async () => {
    if (!userId) return;
    
    try {
      const customersData = await getDocuments(userId, 'customers');
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const generateReceiptText = () => {
    if (!lastSale) return '';
    
    let text = `🏥 *${userData?.pharmacyName || 'NomoFarma'}* 🏥\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    if (userData?.rfc) {
      text += `📍 RFC/NIT: ${userData.rfc}\n`;
    }
    if (userData?.businessAddress) {
      text += `📍 ${userData.businessAddress}\n`;
    }
    if (userData?.businessCity) {
      text += `📍 ${userData.businessCity}\n`;
    }
    if (userData?.businessPhone) {
      text += `📞 Tel: ${userData.businessPhone}\n`;
    }
    if (userData?.businessEmail) {
      text += `📧 ${userData.businessEmail}\n`;
    }
    text += `📅 ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}\n`;
    text += `👤 Cliente: ${lastSale.customerName || 'Público General'}\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `\n🛒 *PRODUCTOS COMPRADOS*\n\n`;
    
    lastSale.items.forEach((item, index) => {
      text += `${index + 1}. 💊 ${item.productName}\n`;
      text += `   📦 Cantidad: ${item.quantity} x ${formatCurrency(item.unitPrice)}\n`;
      text += `   💰 Subtotal: ${formatCurrency(item.total)}\n\n`;
    });
    
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `💳 *RESUMEN DE PAGO*\n\n`;
    text += `Subtotal: ${formatCurrency(lastSale.subtotal)}\n`;
    
    if (lastSale.chargedWithTax !== false) {
      text += `IVA 16%: ${formatCurrency(lastSale.tax)}\n`;
    }
    
    text += `\n✅ *TOTAL A PAGAR: ${formatCurrency(lastSale.total)}* ✅\n\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `🙏 *¡Gracias por su compra!* 🙏\n`;
    text += `📋 Conserve este recibo para cualquier aclaración\n`;
    text += `💚 ¡Que tenga un excelente día!`;
    
    return text;
  };

  const sendWhatsApp = () => {
    if (!lastSale) return;
    
    const text = generateReceiptText();
    const encodedText = encodeURIComponent(text);
    
    // Si hay número de teléfono del cliente, usarlo; si no, abrir sin número
    let phoneNumber = lastSale.customerPhone || '';
    
    // Limpiar el número de teléfono (remover espacios, guiones, paréntesis)
    phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Si no empieza con código de país, agregar código de Colombia (57)
    if (phoneNumber && !phoneNumber.startsWith('+')) {
      phoneNumber = '57' + phoneNumber;
    }
    
    const whatsappUrl = phoneNumber 
      ? `https://wa.me/${phoneNumber}?text=${encodedText}`
      : `https://wa.me/?text=${encodedText}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const handlePrint = () => {
    if (!receiptRef.current) {
      console.error('No se encontró el elemento del recibo');
      alert('Error: No se puede imprimir el recibo');
      return;
    }

    try {
      // Crear un iframe oculto para imprimir
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.top = '-10000px';
      printFrame.style.left = '-10000px';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow || printFrame.contentDocument;
      const doc = printDocument.document || printDocument;
      
      // Escribir el contenido HTML
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Recibo_${lastSale?.saleNumber || 'venta'}</title>
            <style>
              /* Version 2.0 - Tamaño grande para POS */
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              @page {
                size: A6;
                margin: 0;
              }
              body {
                font-family: 'Courier New', monospace;
                font-size: 20px !important;
                line-height: 1.6;
                margin: 0;
                padding: 1mm 0.5mm 30mm 0.5mm; /* Agregado 30mm de espacio inferior */
                width: 100%;
                max-width: 105mm;
              }
              .receipt {
                width: 100%;
              }
              .receipt-header {
                text-align: center;
                margin-bottom: 15px;
              }
              .receipt-header h2 {
                font-size: 32px !important;
                font-weight: bold;
                margin-bottom: 8px;
              }
              .receipt-header p {
                font-size: 18px !important;
                margin: 4px 0;
              }
              .receipt-body {
                margin: 15px 0;
              }
              .receipt-item {
                display: flex;
                justify-content: space-between;
                margin: 8px 0;
                font-size: 18px !important;
                font-weight: normal;
              }
              .receipt-item strong {
                font-weight: bold;
              }
              .divider {
                border-top: 1px dashed #000;
                margin: 10px 0;
                padding: 10px 0;
              }
              .receipt-footer {
                text-align: center;
                margin-top: 12px;
                padding-top: 12px;
                padding-bottom: 20mm; /* Espacio adicional al final */
                border-top: 1px dashed #000;
              }
              .receipt-footer p {
                font-size: 16px !important;
                margin: 4px 0;
              }
              @media print {
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  height: auto;
                }
                body {
                  padding: 1mm 0.5mm 30mm 0.5mm; /* Mantener el espacio inferior en print */
                }
                @page {
                  margin: 0;
                  size: A6;
                }
              }
            </style>
          </head>
          <body>
            ${receiptRef.current.innerHTML}
          </body>
        </html>
      `);
      doc.close();
      
      // Esperar a que se cargue el contenido
      printFrame.onload = function() {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          // Remover el iframe después de imprimir
          setTimeout(() => {
            document.body.removeChild(printFrame);
            console.log('Impresión completada');
          }, 1000);
        } catch (e) {
          console.error('Error durante la impresión:', e);
          document.body.removeChild(printFrame);
        }
      };
      
    } catch (error) {
      console.error('Error al imprimir:', error);
      alert('Error al imprimir el recibo. Por favor, intente nuevamente.');
    }
  };

  const handleBarcodeScanned = async (code) => {
    // Evitar procesamiento múltiple del mismo código
    if (isProcessingBarcode) return;
    
    setIsProcessingBarcode(true);
    
    // Retrasar el cierre del scanner para evitar conflictos con la animación
    setTimeout(() => {
      setShowScanner(false);
      setShowPhysicalScanner(false);
    }, 100);
    
    try {
      // Buscar directamente en el inventario
      const product = products.find(p => p.barcode === code);
      
      if (product) {
        addToCart(product);
        setSearchTerm('');
        setSearchResults([]);
      } else {
        // Si no se encuentra, buscar en la base de datos por si acaso
        try {
          const results = await getDocuments(userId, 'products', [
            { field: 'barcode', operator: '==', value: code }
          ]);
          
          if (results.length > 0 && results[0].stock > 0) {
            addToCart(results[0]);
            // Actualizar la lista de productos localmente
            setProducts(prev => [...prev, results[0]]);
          } else {
            alert('Producto no encontrado en el inventario o sin stock disponible');
          }
        } catch (error) {
          console.error('Error buscando producto:', error);
          alert('Error al buscar el producto');
        }
      }
    } finally {
      // Esperar un momento antes de permitir otro escaneo
      setTimeout(() => {
        setIsProcessingBarcode(false);
      }, 1000);
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    
    if (existingItem) {
      if (existingItem.quantity < product.stock) {
        setCart(cart.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1, customPrice: item.customPrice || null }
            : item
        ));
        // Mostrar animación solo si no está ya activa
        if (!showAddToCartAnimation) {
          setShowAddToCartAnimation(true);
          setAnimationKey(prev => prev + 1);
          // Ocultar animación después de completarse
          setTimeout(() => {
            setShowAddToCartAnimation(false);
          }, 1000);
        }
      } else {
        alert('No hay suficiente stock disponible');
      }
    } else {
      setCart([...cart, { ...product, quantity: 1, customPrice: null }]);
      // Mostrar animación solo si no está ya activa
      if (!showAddToCartAnimation) {
        setShowAddToCartAnimation(true);
        setAnimationKey(prev => prev + 1);
        // Ocultar animación después de completarse
        setTimeout(() => {
          setShowAddToCartAnimation(false);
        }, 1000);
      }
    }
  };

  const updateQuantity = (productId, change) => {
    const item = cart.find(item => item.id === productId);
    const product = products.find(p => p.id === productId);
    
    if (item.quantity + change <= 0) {
      removeFromCart(productId);
    } else if (item.quantity + change <= product.stock) {
      setCart(cart.map(item => 
        item.id === productId 
          ? { ...item, quantity: item.quantity + change }
          : item
      ));
    } else {
      alert('No hay suficiente stock disponible');
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const price = item.customPrice !== null ? item.customPrice : item.salePrice;
      return sum + (price * item.quantity);
    }, 0);
  };

  const calculateTax = () => {
    return chargeWithTax ? calculateSubtotal() * 0.16 : 0; // 16% IVA
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleCompleteSaleClick = () => {
    if (cart.length === 0) {
      alert('El carrito está vacío');
      return;
    }
    
    // Cargar clientes antes de mostrar el modal
    loadCustomers();
    setShowCustomerModal(true);
  };

  const completeSale = async () => {
    if (!selectedCustomer && !showNewCustomerForm) {
      alert('Por favor seleccione un cliente o cree uno nuevo');
      return;
    }

    // Verificar stock antes de completar la venta
    for (const item of cart) {
      const currentProduct = products.find(p => p.id === item.id);
      if (!currentProduct || currentProduct.stock < item.quantity) {
        alert(`Stock insuficiente para ${item.name}. Stock actual: ${currentProduct?.stock || 0}`);
        return;
      }
    }

    try {
      // Si es un cliente nuevo, primero lo creamos
      let customerId = selectedCustomer?.id;
      let customerName = selectedCustomer?.name;
      let customerPhone = selectedCustomer?.phone;
      
      if (showNewCustomerForm) {
        try {
          const customerData = {
            name: newCustomerData.name,
            document: newCustomerData.document || '',
            email: newCustomerData.email || '',
            phone: newCustomerData.phone || '',
            address: newCustomerData.address || '',
            createdAt: new Date().toISOString()
          };
          
          const newCustomerId = await addDocument(userId, 'customers', customerData);
          customerId = newCustomerId;
          customerName = newCustomerData.name;
          customerPhone = newCustomerData.phone;
        } catch (error) {
          console.error('Error creating customer:', error);
          alert('Error al crear el cliente');
          return;
        }
      }
      // Validar que tengamos los datos del cliente
      if (!customerId || !customerName) {
        alert('Error: No se pudo obtener la información del cliente');
        return;
      }
      
      // Obtener el número secuencial de venta
      const saleNumber = await getNextSaleNumber(userId);
      
      const saleData = {
        saleNumber: saleNumber, // Número secuencial de venta
        items: cart.map(item => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          unitPrice: item.customPrice !== null ? item.customPrice : item.salePrice,
          originalPrice: item.salePrice,
          customPrice: item.customPrice,
          total: (item.customPrice !== null ? item.customPrice : item.salePrice) * item.quantity
        })),
        subtotal: calculateSubtotal(),
        tax: calculateTax(),
        total: calculateTotal(),
        date: new Date().toISOString(),
        status: 'completed',
        chargedWithTax: chargeWithTax,
        customerId: customerId || 'general',
        customerName: customerName || 'Público General',
        customerPhone: customerPhone || ''
      };

      const saleId = await addDocument(userId, 'sales', saleData);
      
      for (const item of cart) {
        const newStock = item.stock - item.quantity;
        await updateDocument(userId, 'products', item.id, { stock: newStock });
      }

      setLastSale({ id: saleId, ...saleData });
      setShowReceipt(true);
      setCart([]);
      await loadProducts();
      
      // Limpiar estados del cliente
      setShowCustomerModal(false);
      setSelectedCustomer(null);
      setShowNewCustomerForm(false);
      setNewCustomerData({
        name: '',
        document: '',
        email: '',
        phone: '',
        address: ''
      });
      setCustomerSearchTerm('');
    } catch (error) {
      console.error('Error completing sale:', error);
      alert('Error al completar la venta');
    }
  };

  // Buscar productos cuando el usuario escribe
  useEffect(() => {
    const searchProducts = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const filtered = products.filter(product => 
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.barcode.includes(searchTerm) ||
          product.laboratory.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setSearchResults(filtered);
      } catch (error) {
        console.error('Error buscando productos:', error);
      } finally {
        setSearching(false);
      }
    };

    const timeoutId = setTimeout(searchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, products]);

  return (
    <PageContainer>
        <PageTitleContainer>
        <Lottie 
          animationData={pharmacyStoreAnimation} 
          loop={true}
          style={{ width: 100, height: 100 }}
        />
        <PageTitle>Nueva Venta</PageTitle>
        <Lottie 
          animationData={pharmacyStoreAnimation} 
          loop={true}
          style={{ width: 100, height: 100 }}
        />
      </PageTitleContainer>
      
      <SearchContainer isTyping={searchTerm.length > 0 || cart.length > 0}>
        <SearchInput
          type="text"
          placeholder={isMobile ? "Buscar producto..." : "Buscar producto por nombre o código..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: 0 }}
          autoFocus
          isTyping={searchTerm.length > 0 || cart.length > 0}
        />
        <ScannerButton 
          onClick={() => {
            if (hasPhysicalScanner) {
              setShowPhysicalScanner(true);
            } else {
              setShowScanner(true);
            }
          }}
          title="Escanear código de barras"
          isTyping={searchTerm.length > 0 || cart.length > 0}
        >
          <div className="lottie-wrapper">
            <Lottie 
              animationData={scanningBarcodeButtonAnimation} 
              loop={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </ScannerButton>
      </SearchContainer>

      {searchTerm.length >= 2 && (
        searching ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#7F8C8D' }}>
            Buscando productos...
          </p>
        ) : searchResults.length > 0 ? (
          <ProductsList>
            <p style={{ marginBottom: '10px', color: '#7F8C8D' }}>
              {searchResults.length} producto(s) encontrado(s)
            </p>
            {searchResults.map(product => (
              <ProductItem 
                key={product.id}
                onClick={() => {
                  if (product.stock > 0) {
                    addToCart(product);
                    setSearchTerm('');
                    setSearchResults([]);
                  }
                }}
                disabled={product.stock === 0}
                className={
                  product.stock === 0 ? 'no-stock disabled' : 
                  product.stock <= 5 ? 'low-stock' : ''
                }
              >
                <div>
                  <h4>{product.name}</h4>
                  <p style={{ fontSize: '14px', color: '#7F8C8D' }}>
                    {product.laboratory} | Stock: {product.stock}
                    {product.stock === 0 && <span style={{ color: '#E74C3C', fontWeight: 'bold' }}> (Agotado)</span>}
                    {product.stock > 0 && product.stock <= 5 && <span style={{ color: '#F39C12', fontWeight: 'bold' }}> (Bajo)</span>}
                  </p>
                </div>
                <div style={{ 
                  textAlign: 'right',
                  '@media (max-width: 768px)': {
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '8px'
                  }
                }}>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', color: product.stock === 0 ? '#95A5A6' : '#27AE60' }}>
                    {formatCurrency(product.salePrice)}
                  </p>
                  {product.stock > 0 && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#3498DB', 
                      marginTop: '4px',
                      '@media (max-width: 768px)': {
                        marginTop: 0,
                        marginLeft: '16px'
                      }
                    }}>
                      Tocar para agregar
                    </p>
                  )}
                </div>
              </ProductItem>
            ))}
          </ProductsList>
        ) : (
          <p style={{ textAlign: 'center', padding: '20px', color: '#7F8C8D' }}>
            No se encontraron productos con "{searchTerm}"
          </p>
        )
      )}


      {cart.length > 0 && (
        <div style={{ 
          borderTop: '2px solid #E8E8E8', 
          marginTop: '16px', 
          paddingTop: '16px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0 }}>Productos Agregados</h3>
          <p style={{ fontSize: '12px', color: '#7F8C8D', margin: 0 }}>
            💡 Haz clic en el precio para editarlo
          </p>
        </div>
      )}

      <CartSection>
        <div>
          <CartHeader hasItems={cart.length > 0}>
            <div className="title-section">
              <Lottie 
                animationData={shoppingCartAnimation} 
                loop={true}
                style={{ 
                  width: cart.length > 0 ? 40 : 80, 
                  height: cart.length > 0 ? 40 : 80,
                  transition: 'all 0.3s ease-in-out'
                }}
              />
              <h3>Carrito de Compras</h3>
              {cart.length > 0 && (
                <div className="cart-count">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)} {cart.reduce((sum, item) => sum + item.quantity, 0) === 1 ? 'producto' : 'productos'}
                </div>
              )}
            </div>
          </CartHeader>
          {cart.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#7F8C8D' }}>
              Carrito vacío
            </p>
          ) : (
            <>
              {cart.map(item => (
                <CartItem key={item.id}>
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      fontWeight: '600', 
                      marginBottom: '6px',
                      fontSize: '16px',
                      color: '#2C3E50',
                      lineHeight: '1.3'
                    }}>
                      {item.name}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {editingPrice === item.id ? (
                        <Input
                          type="text"
                          value={tempPrice}
                          onChange={(e) => setTempPrice(formatInputNumber(e.target.value))}
                          onBlur={() => {
                            const newPrice = parseFormattedNumber(tempPrice);
                            if (!isNaN(newPrice) && newPrice > 0) {
                              setCart(cart.map(cartItem => 
                                cartItem.id === item.id 
                                  ? { ...cartItem, customPrice: newPrice }
                                  : cartItem
                              ));
                            }
                            setEditingPrice(null);
                            setTempPrice('');
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              const newPrice = parseFormattedNumber(tempPrice);
                              if (!isNaN(newPrice) && newPrice > 0) {
                                setCart(cart.map(cartItem => 
                                  cartItem.id === item.id 
                                    ? { ...cartItem, customPrice: newPrice }
                                    : cartItem
                                ));
                              }
                              setEditingPrice(null);
                              setTempPrice('');
                            }
                          }}
                          style={{ width: '100px', height: '30px', fontSize: '14px' }}
                          autoFocus
                        />
                      ) : (
                        <p 
                          style={{ 
                            fontSize: '15px', 
                            color: item.customPrice !== null ? '#E67E22' : '#27AE60',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: '500',
                            margin: 0
                          }}
                          onClick={() => {
                            setEditingPrice(item.id);
                            setTempPrice(formatNumber(item.customPrice !== null ? item.customPrice : item.salePrice));
                          }}
                        >
                          {formatCurrency(item.customPrice !== null ? item.customPrice : item.salePrice)} c/u
                        </p>
                      )}
                      {item.customPrice !== null && (
                        <span style={{ fontSize: '12px', color: '#95A5A6', textDecoration: 'line-through' }}>
                          {formatCurrency(item.salePrice)}
                        </span>
                      )}
                    </div>
                  </div>
                  <QuantityControl>
                    <QuantityButton onClick={() => updateQuantity(item.id, -1)}>
                      -
                    </QuantityButton>
                    <span style={{ 
                      margin: '0 8px', 
                      minWidth: '30px', 
                      textAlign: 'center',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2C3E50'
                    }}>
                      {item.quantity}
                    </span>
                    <QuantityButton onClick={() => updateQuantity(item.id, 1)}>
                      +
                    </QuantityButton>
                    <Button 
                      size="small" 
                      variant="ghost"
                      onClick={() => removeFromCart(item.id)}
                      style={{ marginLeft: '8px', color: '#E74C3C' }}
                    >
                      ✕
                    </Button>
                  </QuantityControl>
                </CartItem>
              ))}

              <TotalSection>
                <TotalRow>
                  <span>Subtotal:</span>
                  <span>{formatCurrency(calculateSubtotal())}</span>
                </TotalRow>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: chargeWithTax ? 'transparent' : '#FFF3CD',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    color: chargeWithTax ? 'inherit' : '#856404'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={chargeWithTax}
                      onChange={(e) => setChargeWithTax(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Cobrar con IVA
                  </label>
                  {!chargeWithTax && <span style={{ fontSize: '12px', color: '#856404' }}>Sin IVA</span>}
                </div>
                {chargeWithTax && (
                  <TotalRow>
                    <span>IVA (16%):</span>
                    <span>{formatCurrency(calculateTax())}</span>
                  </TotalRow>
                )}
                <TotalRow className="total">
                  <span>Total:</span>
                  <span>{formatCurrency(calculateTotal())}</span>
                </TotalRow>
              </TotalSection>

              <Button 
                fullWidth 
                size="large"
                onClick={handleCompleteSaleClick}
                style={{ marginTop: '24px' }}
              >
                Completar Venta
              </Button>
            </>
          )}
        </div>
      </CartSection>

      <ZXingScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScanned}
      />

      <PhysicalBarcodeScanner
        isOpen={showPhysicalScanner}
        onClose={() => setShowPhysicalScanner(false)}
        onScan={handleBarcodeScanned}
      />

      <Modal
        isOpen={showReceipt}
        onClose={() => setShowReceipt(false)}
        title="Recibo de Venta"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowReceipt(false)}>
              Cerrar
            </Button>
            <Button 
              onClick={() => {
                if (lastSale) {
                  sendWhatsApp();
                } else {
                  alert('No hay datos de venta para enviar');
                }
              }}
              style={{ backgroundColor: '#25D366' }}
            >
              📱 WhatsApp
            </Button>
            <Button 
              onClick={() => {
                if (lastSale && receiptRef.current) {
                  handlePrint();
                } else {
                  alert('No hay datos de venta para imprimir');
                }
              }}
            >
              🖨️ Imprimir
            </Button>
            <Button 
              onClick={async () => {
                if (lastSale) {
                  await shareReceiptPDF(lastSale, userData);
                } else {
                  alert('No hay datos de venta para compartir');
                }
              }}
              style={{ backgroundColor: '#e74c3c' }}
            >
              📄 Compartir PDF
            </Button>
          </>
        }
      >
        <Receipt ref={receiptRef} className="receipt">
          <ReceiptHeader className="receipt-header">
            <h2>{userData?.pharmacyName || 'NomoFarma'}</h2>
            {userData?.rfc && <p>RFC/NIT: {userData.rfc}</p>}
            {userData?.businessAddress && <p>{userData.businessAddress}</p>}
            {userData?.businessCity && <p>{userData.businessCity}</p>}
            {userData?.businessPhone && <p>Tel: {userData.businessPhone}</p>}
            {userData?.businessEmail && <p>{userData.businessEmail}</p>}
            <p>{format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
            <p style={{ marginTop: '10px', borderTop: '1px dashed #000', paddingTop: '10px' }}>
              Cliente: {lastSale?.customerName || 'Público General'}
            </p>
          </ReceiptHeader>
          
          <ReceiptBody className="receipt-body">
            <div className="divider">
              {lastSale?.items.map((item, index) => (
                <div key={index}>
                  <ReceiptItem className="receipt-item">
                    <span>{item.productName}</span>
                  </ReceiptItem>
                  <ReceiptItem className="receipt-item">
                    <span>{item.quantity} x {formatCurrency(item.unitPrice)}</span>
                    <span>{formatCurrency(item.total)}</span>
                  </ReceiptItem>
                </div>
              ))}
            </div>
            
            <div style={{ marginTop: '10px' }}>
              <ReceiptItem className="receipt-item">
                <span>Subtotal:</span>
                <span>{formatCurrency(lastSale?.subtotal)}</span>
              </ReceiptItem>
              {lastSale?.chargedWithTax !== false && (
                <ReceiptItem className="receipt-item">
                  <span>IVA 16%:</span>
                  <span>{formatCurrency(lastSale?.tax)}</span>
                </ReceiptItem>
              )}
              <ReceiptItem className="receipt-item" style={{ fontWeight: 'bold', fontSize: '24px', marginTop: '10px', paddingTop: '8px', borderTop: '2px solid #000' }}>
                <span>TOTAL:</span>
                <span>{formatCurrency(lastSale?.total)}</span>
              </ReceiptItem>
            </div>
          </ReceiptBody>
          
          <ReceiptFooter className="receipt-footer">
            <p>¡Gracias por su compra!</p>
            <p>Conserve su ticket</p>
            <p>No. Ticket: {lastSale?.saleNumber}</p>
          </ReceiptFooter>
        </Receipt>
      </Modal>

      <Modal
        isOpen={showCustomerModal}
        onClose={() => {
          setShowCustomerModal(false);
          setSelectedCustomer(null);
          setShowNewCustomerForm(false);
          setCustomerSearchTerm('');
        }}
        title="Seleccionar Cliente"
        size="small"
      >
        <CustomerModalContainer>
          {!showNewCustomerForm ? (
            <>
              <CustomerSearchSection>
                <Input
                  type="text"
                  placeholder="Buscar cliente por nombre o cédula..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                />
              </CustomerSearchSection>
              
              <CustomerList>
                {customers
                  .filter(customer => {
                    const searchLower = customerSearchTerm.toLowerCase();
                    return (customer.name && customer.name.toLowerCase().includes(searchLower)) ||
                           (customer.document && customer.document.toLowerCase().includes(searchLower));
                  })
                  .map(customer => (
                    <CustomerCard
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      selected={selectedCustomer?.id === customer.id}
                    >
                      <CustomerName>{customer.name || 'Sin nombre'}</CustomerName>
                      <CustomerInfo>CC: {customer.document || 'Sin documento'}</CustomerInfo>
                      <CustomerInfo>
                        {customer.phone || 'Sin teléfono'} | {customer.email || 'Sin email'}
                      </CustomerInfo>
                    </CustomerCard>
                  ))}
              </CustomerList>
              
              <div style={{ marginBottom: '16px' }}>
                <PublicGeneralButton 
                  variant="text"
                  fullWidth
                  selected={selectedCustomer?.id === 'general'}
                  onClick={() => {
                    setSelectedCustomer({ id: 'general', name: 'Público General' });
                  }}
                >
                  Venta a Público General (Sin Cliente)
                </PublicGeneralButton>
              </div>
              
              {selectedCustomer?.id === 'general' && (
                <div style={{
                  padding: '16px',
                  backgroundColor: 'rgba(135, 206, 235, 0.1)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid rgba(135, 206, 235, 0.3)',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 12px 0', color: '#3498db', fontWeight: '600' }}>
                    ✓ Venta a Público General seleccionada
                  </p>
                  <Button 
                    fullWidth
                    onClick={completeSale}
                    style={{ backgroundColor: '#3498db', borderColor: '#3498db' }}
                  >
                    Completar Venta Sin Cliente
                  </Button>
                </div>
              )}
              
              <ButtonGroup>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => setShowNewCustomerForm(true)}
                >
                  Cliente Nuevo
                </Button>
                <Button 
                  fullWidth
                  disabled={!selectedCustomer || selectedCustomer?.id === 'general'}
                  onClick={completeSale}
                  style={{
                    backgroundColor: selectedCustomer && selectedCustomer?.id !== 'general' ? '#2ecc71' : undefined,
                    borderColor: selectedCustomer && selectedCustomer?.id !== 'general' ? '#2ecc71' : undefined
                  }}
                >
                  {selectedCustomer && selectedCustomer?.id !== 'general' ? 
                    `Continuar con ${selectedCustomer.name}` : 
                    'Continuar con Venta'}
                </Button>
              </ButtonGroup>
            </>
          ) : (
            <FormSection>
              <FormTitle>Nuevo Cliente</FormTitle>
              <Input
                type="text"
                placeholder="Nombre completo *"
                value={newCustomerData.name}
                onChange={(e) => setNewCustomerData({...newCustomerData, name: e.target.value})}
              />
              <Input
                type="text"
                placeholder="Cédula/Documento (CC) *"
                value={newCustomerData.document}
                onChange={(e) => setNewCustomerData({...newCustomerData, document: e.target.value})}
              />
              <Input
                type="tel"
                placeholder="Teléfono"
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
              />
              <Input
                type="email"
                placeholder="Email"
                value={newCustomerData.email}
                onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
              />
              <Input
                type="text"
                placeholder="Dirección"
                value={newCustomerData.address}
                onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})}
              />
              
              <ButtonGroup>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => {
                    setShowNewCustomerForm(false);
                    setNewCustomerData({
                      name: '',
                      document: '',
                      email: '',
                      phone: '',
                      address: ''
                    });
                  }}
                >
                  Cancelar
                </Button>
                <Button 
                  fullWidth
                  disabled={!newCustomerData.name || !newCustomerData.document}
                  onClick={completeSale}
                >
                  Crear y Continuar
                </Button>
              </ButtonGroup>
            </FormSection>
          )}
        </CustomerModalContainer>
      </Modal>

      {showAddToCartAnimation && (
        <AnimationContainer key={`animation-${animationKey}`}>
          <div className="success-checkmark" />
        </AnimationContainer>
      )}
    </PageContainer>
  );
};